import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Bell, BellRing, Fingerprint, KeyRound, LogOut, Globe, User } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import appApi, { setPin, checkPin, hasPin, clearPin, bioAvailable, bioEnrolled, bioEnroll, bioClear, pushSubscribe, pushUnsubscribe, pushSubscribed, pushTest } from "./appApi";
import { spring, stagger, rise } from "./bits";

const Card = ({ children }) => (
  <motion.div variants={rise} className="rounded-2xl bg-white border border-brand/10 p-4 shadow-sm">{children}</motion.div>
);

const SettingsScreen = ({ user, onLogout }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [pushOn, setPushOn] = useState(pushSubscribed());
  const [bioOn, setBioOn] = useState(bioEnrolled());
  const [bioOk, setBioOk] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinForm, setPinForm] = useState({ current: "", next: "" });

  useEffect(() => { bioAvailable().then(setBioOk); }, []);

  const togglePush = async () => {
    try {
      if (pushOn) {
        await pushUnsubscribe();
        setPushOn(false);
      } else {
        await pushSubscribe();
        setPushOn(true);
        toast.success(m.set_notif_on);
      }
    } catch (err) {
      toast.error(err.message === "denied" ? m.set_notif_denied : m.error_generic);
    }
  };

  const testPush = async () => {
    try { await pushTest(); toast.success(m.set_notif_on); } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
  };

  const toggleBio = async () => {
    if (bioOn) { bioClear(); setBioOn(false); return; }
    try { await bioEnroll(user); setBioOn(true); } catch { toast.error(m.error_generic); }
  };

  const changePin = async () => {
    if (hasPin() && !(await checkPin(user.id, pinForm.current))) {
      toast.error(m.set_pin_wrong_current);
      return;
    }
    if (!/^\d{4}$/.test(pinForm.next)) return;
    await setPin(user.id, pinForm.next);
    toast.success(m.set_pin_changed);
    setPinOpen(false);
    setPinForm({ current: "", next: "" });
  };

  const canNotif = !!user.perms?.notifications;

  return (
    <div data-testid="app-settings-screen">
      <h1 className="font-heading text-3xl font-medium text-brand-ink">{m.settings_title}</h1>
      <motion.div variants={stagger} initial="hidden" animate="show" className="mt-5 space-y-3">
        <Card>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand-soft text-brand"><User className="h-5 w-5" /></div>
            <div>
              <p className="text-[11px] font-bold uppercase text-brand-ink/40">{m.set_user}</p>
              <p className="font-bold text-brand-ink" data-testid="settings-user-name">{user.name}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand-soft text-brand"><KeyRound className="h-5 w-5" /></div>
            <p className="font-bold text-brand-ink flex-1">{m.set_change_pin}</p>
            <motion.button whileTap={{ scale: 0.94 }} onClick={() => setPinOpen(!pinOpen)} className="rounded-full bg-brand-soft/70 text-brand text-xs font-bold px-4 py-2" data-testid="settings-pin-toggle">
              {m.set_change_pin}
            </motion.button>
          </div>
          {pinOpen && (
            <div className="mt-3 pt-3 border-t border-brand/10 space-y-2">
              {hasPin() && (
                <input value={pinForm.current} onChange={(e) => setPinForm({ ...pinForm, current: e.target.value })} placeholder={m.set_current_pin} inputMode="numeric" maxLength={4} className="w-full h-11 rounded-xl border border-brand/20 px-4 focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="settings-pin-current" />
              )}
              <div className="flex gap-2">
                <input value={pinForm.next} onChange={(e) => setPinForm({ ...pinForm, next: e.target.value })} placeholder={m.set_new_pin} inputMode="numeric" maxLength={4} className="flex-1 h-11 rounded-xl border border-brand/20 px-4 focus:outline-none focus:ring-2 focus:ring-brand/40" data-testid="settings-pin-new" />
                <motion.button whileTap={{ scale: 0.94 }} onClick={changePin} className="h-11 px-5 rounded-xl bg-brand text-brand-cream font-bold" data-testid="settings-pin-save">{m.cust_note_add}</motion.button>
              </div>
            </div>
          )}
        </Card>

        {bioOk && (
          <Card>
            <div className="flex items-center gap-3">
              <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand-soft text-brand"><Fingerprint className="h-5 w-5" /></div>
              <p className="font-bold text-brand-ink flex-1">{m.set_bio}</p>
              <button
                onClick={toggleBio}
                className={`w-14 h-8 rounded-full p-1 transition-colors ${bioOn ? "bg-brand" : "bg-brand/15"}`}
                data-testid="settings-bio-toggle"
                aria-label="biometrics"
              >
                <motion.span layout transition={spring} className={`block h-6 w-6 rounded-full bg-white shadow ${bioOn ? "ml-6" : "ml-0"}`} />
              </button>
            </div>
          </Card>
        )}

        <Card>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand-soft text-brand">{pushOn ? <BellRing className="h-5 w-5" /> : <Bell className="h-5 w-5" />}</div>
            <div className="flex-1">
              <p className="font-bold text-brand-ink">{m.set_notif}</p>
              <p className="text-[11px] text-brand-ink/50">{canNotif ? m.set_notif_sub : m.set_notif_no_perm}</p>
            </div>
            {canNotif && (
              <button
                onClick={togglePush}
                className={`w-14 h-8 rounded-full p-1 transition-colors ${pushOn ? "bg-brand" : "bg-brand/15"}`}
                data-testid="settings-push-toggle"
                aria-label="notifications"
              >
                <motion.span layout transition={spring} className={`block h-6 w-6 rounded-full bg-white shadow ${pushOn ? "ml-6" : "ml-0"}`} />
              </button>
            )}
          </div>
          {canNotif && pushOn && (
            <motion.button whileTap={{ scale: 0.95 }} onClick={testPush} className="mt-3 w-full h-10 rounded-xl border border-brand/20 text-brand font-bold text-sm" data-testid="settings-push-test">
              {m.set_notif_test}
            </motion.button>
          )}
        </Card>

        <Card>
          <a href="/admin" target="_blank" rel="noreferrer" className="flex items-center gap-3 no-a11y-underline" data-testid="settings-web-panel">
            <div className="grid place-items-center h-11 w-11 rounded-xl bg-brand-soft text-brand"><Globe className="h-5 w-5" /></div>
            <p className="font-bold text-brand-ink">{m.set_web}</p>
          </a>
        </Card>

        <motion.button
          variants={rise}
          whileTap={{ scale: 0.97 }}
          onClick={onLogout}
          className="w-full h-13 py-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-600 font-bold flex items-center justify-center gap-2"
          data-testid="settings-logout"
        >
          <LogOut className="h-5 w-5" /> {m.set_logout}
        </motion.button>
      </motion.div>
    </div>
  );
};

export default SettingsScreen;
